/**
 * Am I running inside the desktop app?
 *
 * The desktop shell renders this exact build — there is no separate desktop bundle. It announces
 * itself by exposing `window.stride` from its preload, so the answer is a RUNTIME question, not a
 * `VITE_*` flag baked in at build time. That matters for a reason this codebase has been bitten by
 * before: a build-time flag means two builds of the SPA, and two builds drift. One artifact that
 * asks its host what it is cannot.
 *
 * When present, three things change:
 *   · `getAccessToken()` returns the loopback token instead of asking MSAL (apiClient.ts)
 *   · the sign-in gate is skipped — there is no tenant to sign in to (useAuth.ts, App.tsx)
 *   · the session is a real one, NOT the dev bypass: it was authenticated by a per-launch secret
 *
 * `window` is a parameter everywhere below so both states are reachable in one test run. A
 * module-scope read would fix the answer at import and leave one branch permanently unexercised —
 * see the config rule in CLAUDE.md.
 */

/** What the Electron preload exposes. Mirrors `StrideBridge` in `apps/stride-desktop`. */
export interface StrideLocalBridge {
  /** The per-launch token every `/api` request and socket handshake must carry. */
  token: string
  user: string
  appVersion: string
  platform: string
  dataDir: string
}

declare global {
  interface Window {
    stride?: unknown
  }
}

/**
 * The bridge, or null when this is an ordinary browser.
 *
 * Validated rather than trusted: a bridge with no usable token is treated as absent, so the app
 * falls back to the normal sign-in path instead of sending `Bearer undefined` and getting a 401 it
 * cannot explain.
 */
export function readLocalBridge(win: Pick<Window, 'stride'> | undefined = globalThis.window): StrideLocalBridge | null {
  const candidate = win?.stride as Partial<StrideLocalBridge> | undefined
  if (!candidate || typeof candidate.token !== 'string' || candidate.token.length === 0) return null

  return {
    token: candidate.token,
    user: typeof candidate.user === 'string' ? candidate.user : 'local@stride',
    appVersion: typeof candidate.appVersion === 'string' ? candidate.appVersion : 'unknown',
    platform: typeof candidate.platform === 'string' ? candidate.platform : 'unknown',
    dataDir: typeof candidate.dataDir === 'string' ? candidate.dataDir : '',
  }
}

/** True in the desktop app. */
export function isLocalMode(win?: Pick<Window, 'stride'> | undefined): boolean {
  return readLocalBridge(win) !== null
}

/** The bearer token for local mode, or null when not in it. */
export function localToken(win?: Pick<Window, 'stride'> | undefined): string | null {
  return readLocalBridge(win)?.token ?? null
}
